import React from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../utils/api';
import { format, differenceInDays, isPast, isFuture } from 'date-fns';
import { Icons, Button } from '../shared/ui';

const {
  Document: DocumentTextIcon, Calendar: CalendarIcon, DollarSign: CurrencyDollarIcon,
  Home: HomeIcon, User: UserIcon, CheckCircle2: CheckCircleIcon, Clock: ClockIcon,
  Alert: ExclamationTriangleIcon, Download: ArrowDownTrayIcon, Building: BuildingOfficeIcon,
} = Icons;

const MyContract = () => {
  // Fetch renter's contract
  const { data: contract, isLoading, error } = useQuery({
    queryKey: ['my-contract'],
    queryFn: async () => {
      const response = await api.get('/api/renters/contract');
      return response.data;
    },
    retry: false,
  });

  const getStatusInfo = (status, startDate, endDate) => {
    const today = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    const daysUntilEnd = differenceInDays(end, today);

    // Check if contract dates are in valid range
    const isDateActive = !isPast(end) && !isFuture(start);

    switch (status) {
      case 'active':
        if (daysUntilEnd <= 30 && daysUntilEnd > 0) {
          return {
            label: 'Expiring Soon',
            color: 'text-yellow-600 bg-yellow-100',
            icon: ExclamationTriangleIcon,
            message: `Your contract expires in ${daysUntilEnd} days`,
          };
        }
        return {
          label: 'Active',
          color: 'text-green-600 bg-green-100',
          icon: CheckCircleIcon,
          message: 'Your contract is currently active',
        };
      case 'draft':
        return {
          label: 'Draft',
          color: 'text-teal-600 bg-teal-100',
          icon: DocumentTextIcon,
          message: 'This contract is pending signature',
        };
      case 'pending_signature':
        return {
          label: 'Pending Signature',
          color: 'text-orange-600 bg-orange-100',
          icon: ClockIcon,
          message: 'Waiting for signatures to complete',
        };
      case 'expiring_soon':
        return {
          label: 'Expiring Soon',
          color: 'text-yellow-600 bg-yellow-100',
          icon: ExclamationTriangleIcon,
          message: `Your contract expires in ${daysUntilEnd} days`,
        };
      case 'expired':
        return {
          label: 'Expired',
          color: 'text-red-600 bg-red-100',
          icon: ExclamationTriangleIcon,
          message: 'Your contract has expired. Please contact management.',
        };
      case 'terminated':
        return {
          label: 'Terminated',
          color: 'text-stone-600 bg-stone-100',
          icon: ExclamationTriangleIcon,
          message: 'This contract has been terminated',
        };
      default:
        return {
          label: status || 'Unknown',
          color: 'text-stone-600 bg-stone-100',
          icon: DocumentTextIcon,
          message: '',
        };
    }
  };

  const handleDownload = async () => {
    try {
      const response = await api.get('/api/renters/contract/download', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contract-${contract?.contract_number || 'document'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download contract. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-stone-600">Loading your contract...</p>
        </div>
      </div>
    );
  }

  if (error || !contract) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <DocumentTextIcon className="h-16 w-16 text-stone-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-stone-900 mb-2">No Contract Found</h2>
          <p className="text-stone-600 mb-6">
            You don't have an active contract yet. Please contact management if you believe this is an error.
          </p>
          <div className="bg-teal-50 rounded-lg p-4">
            <p className="text-sm text-teal-800">
              If you recently signed a contract, it may take a few moments to appear here.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const statusInfo = getStatusInfo(contract.status, contract.start_date, contract.end_date);
  const StatusIcon = statusInfo.icon;
  const daysRemaining = differenceInDays(new Date(contract.end_date), new Date());

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">My Contract</h1>
          <p className="text-stone-600 mt-1">View your rental contract details</p>
        </div>
        {contract.document && (
          <Button kind="primary" onClick={handleDownload} iconLeft={<ArrowDownTrayIcon size={16} />}>Download Contract</Button>
        )}
      </div>

      {/* Status Banner */}
      <div className={`rounded-lg p-4 ${statusInfo.color.split(' ')[1]}`}>
        <div className="flex items-center">
          <StatusIcon className={`h-6 w-6 ${statusInfo.color.split(' ')[0]} mr-3`} />
          <div>
            <span className={`font-semibold ${statusInfo.color.split(' ')[0]}`}>
              {statusInfo.label}
            </span>
            {statusInfo.message && (
              <p className={`text-sm ${statusInfo.color.split(' ')[0]} opacity-80`}>
                {statusInfo.message}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Contract Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Contract Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-stone-900 mb-4 flex items-center">
            <DocumentTextIcon className="h-5 w-5 mr-2 text-teal-600" />
            Contract Information
          </h3>
          <dl className="space-y-4">
            <div className="flex justify-between">
              <dt className="text-stone-500">Contract Number</dt>
              <dd className="font-medium text-stone-900">{contract.contract_number}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-stone-500">Status</dt>
              <dd>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                  {statusInfo.label}
                </span>
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-stone-500">Start Date</dt>
              <dd className="font-medium text-stone-900">
                {format(new Date(contract.start_date), 'MMMM d, yyyy')}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-stone-500">End Date</dt>
              <dd className="font-medium text-stone-900">
                {format(new Date(contract.end_date), 'MMMM d, yyyy')}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-stone-500">Contract Duration</dt>
              <dd className="font-medium text-stone-900">
                {Math.ceil((new Date(contract.end_date) - new Date(contract.start_date)) / (1000 * 60 * 60 * 24 * 30))} months
              </dd>
            </div>
            {daysRemaining > 0 && (
              <div className="flex justify-between">
                <dt className="text-stone-500">Days Remaining</dt>
                <dd className={`font-medium ${daysRemaining <= 30 ? 'text-yellow-600' : 'text-green-600'}`}>
                  {daysRemaining} days
                </dd>
              </div>
            )}
            {contract.signed_at && (
              <div className="flex justify-between">
                <dt className="text-stone-500">Signed On</dt>
                <dd className="font-medium text-stone-900">
                  {format(new Date(contract.signed_at), 'MMMM d, yyyy')}
                </dd>
              </div>
            )}
            {contract.template_info && (
              <div className="flex justify-between">
                <dt className="text-stone-500">Contract Template</dt>
                <dd className="font-medium text-stone-900">{contract.template_info.name}</dd>
              </div>
            )}
          </dl>
        </div>

        {/* Property Details */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-stone-900 mb-4 flex items-center">
            <BuildingOfficeIcon className="h-5 w-5 mr-2 text-purple-600" />
            Property Details
          </h3>
          <dl className="space-y-4">
            {contract.building_details && (
              <>
                <div className="flex justify-between">
                  <dt className="text-stone-500">Building</dt>
                  <dd className="font-medium text-stone-900">{contract.building_details.name}</dd>
                </div>
                {contract.building_details.address && (
                  <div className="flex justify-between">
                    <dt className="text-stone-500">Address</dt>
                    <dd className="font-medium text-stone-900 text-right text-sm">{contract.building_details.address}</dd>
                  </div>
                )}
              </>
            )}
            <div className="flex justify-between">
              <dt className="text-stone-500">Room Number</dt>
              <dd className="font-medium text-stone-900">Room {contract.room_number || contract.room_details?.room_number}</dd>
            </div>
            {contract.room_details && (
              <>
                {contract.room_details.floor && (
                  <div className="flex justify-between">
                    <dt className="text-stone-500">Floor</dt>
                    <dd className="font-medium text-stone-900">{contract.room_details.floor}</dd>
                  </div>
                )}
                {contract.room_details.room_type && (
                  <div className="flex justify-between">
                    <dt className="text-stone-500">Room Type</dt>
                    <dd className="font-medium text-stone-900 capitalize">{contract.room_details.room_type}</dd>
                  </div>
                )}
                {contract.room_details.size && (
                  <div className="flex justify-between">
                    <dt className="text-stone-500">Room Size</dt>
                    <dd className="font-medium text-stone-900">{contract.room_details.size} m²</dd>
                  </div>
                )}
                {contract.room_details.max_occupants && (
                  <div className="flex justify-between">
                    <dt className="text-stone-500">Max Occupants</dt>
                    <dd className="font-medium text-stone-900">{contract.room_details.max_occupants}</dd>
                  </div>
                )}
              </>
            )}
          </dl>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-stone-900 mb-4 flex items-center">
          <CurrencyDollarIcon className="h-5 w-5 mr-2 text-green-600" />
          Financial Summary
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <p className="text-sm text-stone-500 mb-1">Monthly Rent</p>
            <p className="text-2xl font-bold text-green-700">₫{contract.monthly_rent?.toLocaleString('vi-VN')}</p>
          </div>
          <div className="bg-teal-50 rounded-lg p-4 text-center">
            <p className="text-sm text-stone-500 mb-1">Security Deposit</p>
            <p className="text-2xl font-bold text-teal-700">₫{contract.deposit_amount?.toLocaleString('vi-VN')}</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 text-center">
            <p className="text-sm text-stone-500 mb-1">Payment Due</p>
            <p className="text-2xl font-bold text-purple-700">Day {contract.payment_due_day}</p>
            <p className="text-xs text-stone-500">of each month</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-4 text-center">
            <p className="text-sm text-stone-500 mb-1">Total Contract Value</p>
            <p className="text-2xl font-bold text-amber-700">
              ₫{(contract.monthly_rent * Math.ceil((new Date(contract.end_date) - new Date(contract.start_date)) / (1000 * 60 * 60 * 24 * 30))).toLocaleString('vi-VN')}
            </p>
            <p className="text-xs text-stone-500">
              ({Math.ceil((new Date(contract.end_date) - new Date(contract.start_date)) / (1000 * 60 * 60 * 24 * 30))} months)
            </p>
          </div>
        </div>
        {contract.template_info?.late_fee_percentage !== undefined && (
          <div className="mt-4 pt-4 border-t">
            <p className="text-sm text-stone-600">
              <span className="font-medium text-red-600">Late Fee:</span> {contract.template_info.late_fee_percentage}% of monthly rent for payments after due date
            </p>
          </div>
        )}
      </div>

      {/* Utility Pricing */}
      {((contract.utility_pricing && Object.keys(contract.utility_pricing).length > 0) ||
        (contract.template_info?.utility_pricing && Object.keys(contract.template_info.utility_pricing).length > 0) ||
        (contract.template_info?.utilities_included?.length > 0)) && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-stone-900 mb-4">Utilities & Services</h3>

          {/* Utility Pricing Table */}
          {(contract.utility_pricing && Object.keys(contract.utility_pricing).length > 0) ||
           (contract.template_info?.utility_pricing && Object.keys(contract.template_info.utility_pricing).length > 0) ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-stone-200">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Utility</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Price</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Unit</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-stone-200">
                  {Object.entries(contract.utility_pricing || contract.template_info?.utility_pricing || {}).map(([key, utility]) => (
                    <tr key={key}>
                      <td className="px-4 py-3 text-sm font-medium text-stone-900 capitalize">{utility.name || key}</td>
                      <td className="px-4 py-3 text-sm text-stone-900">₫{utility.price?.toLocaleString('vi-VN') || 0}</td>
                      <td className="px-4 py-3 text-sm text-stone-500">per {utility.unit || 'month'}</td>
                      <td className="px-4 py-3">
                        {utility.is_included ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircleIcon className="h-3 w-3 mr-1" />
                            Included in Rent
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-stone-100 text-stone-800">
                            Metered / Separate
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {/* Utilities Included List */}
          {contract.template_info?.utilities_included?.length > 0 && (
            <div className={`${(contract.utility_pricing && Object.keys(contract.utility_pricing).length > 0) ? 'mt-4 pt-4 border-t' : ''}`}>
              <p className="text-sm font-medium text-stone-700 mb-2">Utilities Included in Monthly Rent:</p>
              <div className="flex flex-wrap gap-2">
                {contract.template_info.utilities_included.map((utility, idx) => (
                  <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    <CheckCircleIcon className="h-4 w-4 mr-1" />
                    {utility}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Template Info - Auto Renewal */}
      {contract.template_info && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-stone-900 mb-4">Contract Terms</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center">
              <div className={`w-3 h-3 rounded-full mr-2 ${contract.template_info.auto_renewal ? 'bg-green-500' : 'bg-stone-400'}`}></div>
              <span className="text-sm text-stone-600">
                Auto Renewal: <span className="font-medium">{contract.template_info.auto_renewal ? 'Yes' : 'No'}</span>
              </span>
            </div>
            {contract.template_info.deposit_months && (
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-full mr-2 bg-teal-500"></div>
                <span className="text-sm text-stone-600">
                  Deposit: <span className="font-medium">{contract.template_info.deposit_months} month(s)</span>
                </span>
              </div>
            )}
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-full mr-2 bg-purple-500"></div>
              <span className="text-sm text-stone-600">
                Payment Frequency: <span className="font-medium capitalize">{contract.payment_frequency || 'Monthly'}</span>
              </span>
            </div>
          </div>

          {/* Template Clauses */}
          {contract.template_info.clauses?.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm font-medium text-stone-700 mb-2">Standard Contract Clauses:</p>
              <ul className="space-y-1 text-sm text-stone-600">
                {contract.template_info.clauses.map((clause, idx) => (
                  <li key={idx} className="flex">
                    <span className="text-stone-400 mr-2">{idx + 1}.</span>
                    <span>{clause}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Co-Tenants Section */}
      {contract.renters && contract.renters.length > 1 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-stone-900 mb-4 flex items-center">
            <UserIcon className="h-5 w-5 mr-2 text-teal-600" />
            Contract Tenants
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {contract.renters.map((renter) => (
              <div
                key={renter.id}
                className={`p-4 rounded-lg border ${
                  renter.is_primary ? 'border-teal-200 bg-teal-50' : 'border-stone-200 bg-stone-50'
                }`}
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center text-white font-medium">
                    {renter.first_name?.charAt(0) || 'U'}
                  </div>
                  <div className="ml-3">
                    <p className="font-medium text-stone-900">
                      {renter.first_name} {renter.last_name}
                      {renter.is_primary && (
                        <span className="ml-2 text-xs bg-teal-100 text-teal-800 px-2 py-0.5 rounded-full">
                          Primary
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-stone-500">{renter.email}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Terms & Conditions */}
      {contract.terms_and_conditions && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-stone-900 mb-4">Terms & Conditions</h3>
          <div className="prose prose-sm max-w-none text-stone-600">
            <p className="whitespace-pre-wrap">{contract.terms_and_conditions}</p>
          </div>
        </div>
      )}

      {/* Special Clauses */}
      {contract.special_clauses && contract.special_clauses.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-stone-900 mb-4">Special Clauses</h3>
          <ul className="list-disc list-inside space-y-2 text-stone-600">
            {contract.special_clauses.map((clause, index) => (
              <li key={index}>{clause}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Contact Support */}
      <div className="bg-stone-50 rounded-lg p-6 text-center">
        <p className="text-stone-600">
          Have questions about your contract?{' '}
          <a href="/manager/tickets" className="text-teal-600 hover:underline font-medium">
            Submit a ticket
          </a>
          {' '}or contact management.
        </p>
      </div>
    </div>
  );
};

// Helper function for ordinal suffix
function getOrdinalSuffix(num) {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
}

export default MyContract;
